import React, { useState, useEffect } from 'react';
import {
  Phone,
  PhoneCall,
  Clock,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  BarChart3,
  Zap
} from 'lucide-react';
import { useWebSocket, useWebSocketData } from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import clsx from 'clsx';
import { API_BASE_URL } from '../config';

const DashboardOverview = () => {
  const [timeRange, setTimeRange] = useState('24h');
  const [apiData, setApiData] = useState(null);
  const [apiLoading, setApiLoading] = useState(true);
  const { isConnected, requestMetrics, connectionStatus } = useWebSocket();
  const { data: overviewData, loading, error } = useWebSocketData('overview_update');
  const { data: metricsData } = useWebSocketData('metrics_update');
  const { user } = useAuth();

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'system_admin' || user?.email?.includes('@muleworks');

  // Fetch data from REST API
  const fetchDashboardData = async () => {
    try {
      setApiLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/dashboard/overview?timeRange=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setApiData(result.data.metrics);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  useEffect(() => {
    // Request initial data when connected
    if (isConnected) {
      requestMetrics();
    }
  }, [requestMetrics, isConnected]);

  // Fallback data for when WebSocket is not connected
  const defaultMetrics = {
    totalCalls: 0,
    activeCalls: 0,
    avgDuration: 0,
    successRate: 95,
    totalMinutes: 0,
    revenue: 0,
    callsToday: 0,
    uniqueUsers: 0
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) {return '0';}
    if (num >= 1000000) {return `${(num / 1000000).toFixed(1)}M`;}
    if (num >= 1000) {return `${(num / 1000).toFixed(1)}K`;}
    return num.toString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDuration = (seconds) => {
    if (!seconds) {return '0m 0s';}
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getPercentageChange = (current, previous) => {
    if (!previous || previous === 0) {return 0;}
    return ((current - previous) / previous) * 100;
  };

  // Use API data first, then WebSocket data, then fallback
  const metrics = apiData || overviewData || metricsData || defaultMetrics;
  const {
    totalCalls = 0,
    activeCalls = 0,
    averageCallDuration = 0,
    avgCallDuration = averageCallDuration, // Support both field names
    successRate = 0,
    totalRevenue = 0,
    activeUsers = 0,
    systemStatus = 'operational',
    callsThisHour = 0,
    previousPeriodCalls = 0,
    previousPeriodRevenue = 0,
    alertsCount = 0,
    uptime = 99.9
  } = metrics;

  const callsChange = getPercentageChange(totalCalls, previousPeriodCalls);
  const revenueChange = getPercentageChange(totalRevenue, previousPeriodRevenue);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-600 mt-1">
            Real-time insights for your AI voice platform
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {/* Connection Status */}
          <div className="flex items-center space-x-2">
            <div className={clsx(
              'w-2 h-2 rounded-full',
              isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
            )} />
            <span className="text-sm text-gray-600">
              {isConnected ? 'Live Data' : 'Disconnected'}
            </span>
          </div>

          {/* Time Range Selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Connection Status Alert */}
      {!isConnected && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-blue-500 mr-2" />
            <h3 className="text-blue-800 font-medium">Real-time data unavailable</h3>
          </div>
          <p className="text-blue-700 mt-1">Showing demo data. Refresh to reconnect to live metrics.</p>
        </div>
      )}

      {/* System Status Alert */}
      {systemStatus !== 'operational' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-500 mr-2" />
            <h3 className="text-yellow-800 font-medium">System Status: {systemStatus}</h3>
          </div>
          <p className="text-yellow-700 mt-1">Some services may be experiencing issues</p>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Calls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Calls</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(totalCalls)}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Phone className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            {callsChange >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            )}
            <span className={clsx(
              'text-sm font-medium',
              callsChange >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {Math.abs(callsChange).toFixed(1)}%
            </span>
            <span className="text-sm text-gray-500 ml-1">vs previous period</span>
          </div>
        </div>

        {/* Active Calls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Calls</p>
              <p className="text-2xl font-bold text-gray-900">{activeCalls}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <PhoneCall className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <Activity className="w-4 h-4 text-gray-400 mr-1" />
            <span className="text-sm text-gray-500">
              {callsThisHour} calls this hour
            </span>
          </div>
        </div>

        {/* Average Duration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Duration</p>
              <p className="text-2xl font-bold text-gray-900">{formatDuration(averageCallDuration || avgCallDuration)}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <BarChart3 className="w-4 h-4 text-gray-400 mr-1" />
            <span className="text-sm text-gray-500">
              {(successRate || 0).toFixed(1)}% success rate
            </span>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            {revenueChange >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
            )}
            <span className={clsx(
              'text-sm font-medium',
              revenueChange >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {Math.abs(revenueChange).toFixed(1)}%
            </span>
            <span className="text-sm text-gray-500 ml-1">vs previous period</span>
          </div>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Active Users */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Users</p>
              <p className="text-xl font-bold text-gray-900">{activeUsers}</p>
            </div>
            <Users className="w-5 h-5 text-gray-400" />
          </div>
        </div>

        {/* System Uptime */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">System Uptime</p>
              <p className="text-xl font-bold text-gray-900">{uptime}%</p>
            </div>
            <Zap className="w-5 h-5 text-gray-400" />
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Alerts</p>
              <p className="text-xl font-bold text-gray-900">{alertsCount}</p>
            </div>
            <AlertTriangle className={clsx(
              'w-5 h-5',
              alertsCount > 0 ? 'text-red-500' : 'text-gray-400'
            )} />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button className="flex items-center p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
            <Phone className="w-5 h-5 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-900">View Calls</span>
          </button>
          <button className="flex items-center p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
            <BarChart3 className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-900">Analytics</span>
          </button>
          <button className="flex items-center p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors">
            <Users className="w-5 h-5 text-purple-600 mr-2" />
            <span className="text-sm font-medium text-purple-900">Manage Users</span>
          </button>
          <button className="flex items-center p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors">
            <DollarSign className="w-5 h-5 text-yellow-600 mr-2" />
            <span className="text-sm font-medium text-yellow-900">Billing</span>
          </button>
        </div>
      </div>

      {/* Status Indicators - Admin Only */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-900">Voice Processing</p>
                <p className="text-xs text-green-700">Operational</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-900">Telephony</p>
                <p className="text-xs text-green-700">Operational</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-900">AI Services</p>
                <p className="text-xs text-green-700">Operational</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardOverview;